<template>
    <vx-navbar
        v-if="!hiddenForCurrentPath"
        :key="currentPath"
        class="app-navbar"
        :class="{ 'mb-6 md:mb-0': !subnavHidden }"
        aria-label="Main"
    >
        <template v-slot:left="{ narrow }">
            <vx-navbar-action as="div" control :active="isHome()">
                <navbar-logo
                    href="/"
                    data-test="nav-home"
                    aria-label="Home"
                    @click="navigate('/', $event)"
                ></navbar-logo>
            </vx-navbar-action>

            <vx-button
                v-if="!narrow && canUseMessageHub"
                href="/message-hub"
                color="primary"
                size="sm"
                @click="navigate('/message-hub', $event)"
            >
                <font-awesome-icon widthAuto class="text-sm" :icon="faMessages"></font-awesome-icon>
                Message Hub
            </vx-button>

            <vx-button
                v-if="!narrow && showQuickBlastButton"
                href="/messaging/quick-blast"
                color="info"
                size="sm"
                @click="navigate('/messaging/quick-blast', $event)"
            >
                <font-awesome-icon widthAuto class="text-sm" :icon="faRocketLaunch"></font-awesome-icon>
                Quick Blast
            </vx-button>
        </template>

        <template v-slot:nested-before="{ narrow }">
            <template v-if="narrow">
                <li v-if="canUseMessageHub" class="w-full">
                    <vx-button
                        block
                        href="/message-hub"
                        color="primary"
                        size="sm"
                        @click="navigate('/message-hub', $event)"
                    >
                        <font-awesome-icon widthAuto class="text-sm" :icon="faMessages"></font-awesome-icon>
                        Message Hub
                    </vx-button>
                </li>
                <li v-if="showQuickBlastButton" class="w-full">
                    <vx-button
                        block
                        href="/messaging/quick-blast"
                        color="info"
                        size="sm"
                        @click="navigate('/messaging/quick-blast', $event)"
                    >
                        <font-awesome-icon widthAuto class="text-sm" :icon="faRocketLaunch"></font-awesome-icon>
                        Quick Blast
                    </vx-button>
                </li>
            </template>
        </template>

        <vx-navbar-action
            v-for="action in routes"
            :key="action.name"
            :icon="action.icon"
            :href="action.href"
            :target="action.target"
            :rel="action.target ? 'noopener noreferrer' : undefined"
            :active="isActive(action.pathMatcher)"
            :data-test="`navbar-section-${testId(action.name)}`"
            @click="handleLinkClick(action, $event)"
        >
            {{ action.name }}

            <template v-if="action.items?.length" v-slot:items>
                <vx-navbar-action-item
                    v-for="item in action.items"
                    :key="`${action.name}-${item.name}`"
                    :href="item.href"
                    :target="item.target"
                    :rel="item.target ? 'noopener noreferrer' : undefined"
                    :active="isActive(item.pathMatcher, item.href)"
                    :data-test="`navbar-item-${testId(item.name)}`"
                    @click="handleLinkClick(item, $event)"
                >
                    {{ item.name }}
                    <font-awesome-icon
                        v-if="item.target === '_blank'"
                        class="self-center"
                        size="sm"
                        widthAuto
                        :icon="faArrowUpRightFromSquare"
                    ></font-awesome-icon>
                </vx-navbar-action-item>
            </template>
        </vx-navbar-action>

        <template v-slot:nested-after="{ narrow }">
            <navbar-settings v-if="narrow" :metadata="props.metadata"></navbar-settings>
        </template>

        <template v-slot:right="{ narrow }">
            <vx-navbar-action
                v-if="!narrow"
                v-model:open="settingsOpen"
                as="div"
                placement="right"
                data-test="navbar-settings"
                @vue:unmounted="settingsOpen = false"
            >
                <vx-button
                    type="button"
                    color="secondary"
                    size="sm"
                    data-test="settings-menu"
                    aria-label="Settings menu"
                    aria-haspopup="true"
                    :aria-expanded="settingsOpen"
                >
                    <font-awesome-icon widthAuto class="text-sm" :icon="faGear"></font-awesome-icon>
                </vx-button>

                <template v-slot:items>
                    <navbar-settings :metadata="props.metadata"></navbar-settings>
                </template>
            </vx-navbar-action>

            <vx-navbar-action v-if="FEATURES.contact_groups" v-model:open="groupsDropdown" as="div" placement="right">
                <vx-button
                    type="button"
                    color="secondary"
                    size="sm"
                    data-test="context-switcher"
                    aria-haspopup="true"
                    :aria-expanded="groupsDropdown"
                    :aria-keyshortcuts="usesCommandKey ? 'Meta+G' : 'Control+G'"
                >
                    <navbar-context-label></navbar-context-label>
                </vx-button>
                <template v-slot:dropdown>
                    <navbar-context-switch></navbar-context-switch>
                </template>
            </vx-navbar-action>
        </template>
    </vx-navbar>

    <div v-if="!hiddenForCurrentPath && !subnavHidden" class="hidden pt-5 px-7 md:block sm:h-12 sm:mb-8">
        <vx-navigation v-if="activeActionItems.length">
            <vx-navigation-item
                v-for="item in activeActionItems"
                :key="`subnav-${item.name}`"
                :value="item.name"
                :href="item.href"
                :target="item.target"
                :rel="item.target ? 'noopener noreferrer' : undefined"
                :active="isActive(item.pathMatcher, item.href)"
                :data-test="`navbar-subnav-${testId(item.name)}`"
                @click="handleLinkClick(item, $event)"
            >
                {{ item.name }}
                <font-awesome-icon
                    v-if="item.target === '_blank'"
                    size="xs"
                    widthAuto
                    :icon="faArrowUpRightFromSquare"
                ></font-awesome-icon>
            </vx-navigation-item>
        </vx-navigation>
    </div>
</template>

<script setup>
import {
    VxNavbar,
    VxNavbarAction,
    VxNavbarActionItem,
    VxButton,
    VxNavigation,
    VxNavigationItem,
} from '@voxie/frontend-components';
import { navbarRoutes } from './navbar-routes';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faArrowUpRightFromSquare, faGear, faMessages, faRocketLaunch } from '@fortawesome/pro-solid-svg-icons';
import NavbarLogo from './NavbarLogo.vue';
import NavbarSettings from './NavbarSettings.vue';
import NavbarContextSwitch from './NavbarContextSwitch.vue';
import NavbarContextLabel from './NavbarContextLabel.vue';
import { computed, ref, inject, watch } from 'vue';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { CAMPAIGN_ROLES } from '~/constants/roles';
import { useRouterNavigation } from '~/composables/useRouterNavigation';
import { hasOpenModal } from '~/utils/dom';
import { useEventListener } from '@vueuse/core';

const FEATURES = inject('FEATURES');

const { onCorporate, roleOnCurrentOrgUnit, roleOnCurrentGroup, canUseMessageHub } = useCurrentGroup();

const showQuickBlastButton = computed(
    () => onCorporate() || CAMPAIGN_ROLES.includes(roleOnCurrentOrgUnit || roleOnCurrentGroup)
);

const props = defineProps({
    metadata: {
        type: Object,
        default: () => ({
            isServiceRepresentative: false,
            showsTeamSwitcher: false,
            ownsCurrentTeam: false,
            isAdminRoleOnCurrentTeam: false,
            isImpersonator: false,
            isPlatformAdmin: false,
        }),
    },
});

const routes = navbarRoutes(props.metadata);

const { currentMeta, currentPath, navigate } = useRouterNavigation();

const hiddenForCurrentPath = computed(() => Boolean(currentMeta.value.hideAppNavbar));
// A page with its own sub navigation sits flush under the bar rather than under its section's.
const subnavHidden = computed(() => Boolean(currentMeta.value.hideSubnav));

const testId = (name) => name.toLowerCase().replace(/\s+/g, '-');

const isHome = () => {
    return currentPath.value === '/';
};

const isActive = (pathMatcher, href) => {
    if (!pathMatcher) {
        return href == currentPath.value;
    }
    return Boolean(pathMatcher(currentPath.value));
};

const handleLinkClick = (link, event) => {
    link.onClick?.();

    if (link.href) {
        navigate(link.href, event, link.target);
    }
};

const activeActionItems = computed(
    () => routes.find((action) => isActive(action.pathMatcher, action.href))?.items || []
);

const settingsOpen = ref(false);
watch(currentPath, () => {
    settingsOpen.value = false;
});

const usesCommandKey = /iPad|iPhone|Mac OS/.test(navigator.userAgent);
const groupsDropdown = ref(false);
watch(currentPath, () => {
    groupsDropdown.value = false;
});

useEventListener(window, 'keydown', (event) => {
    if (!FEATURES.contact_groups || hiddenForCurrentPath.value) {
        return;
    }
    if ((event.metaKey && event.key === 'g') || (event.ctrlKey && event.key === 'g')) {
        event.preventDefault();
        // It would open out of sight behind the modal.
        if (!hasOpenModal()) {
            groupsDropdown.value = !groupsDropdown.value;
        }
    }
    // The switcher closes itself on an Escape from inside it; this catches one with focus on the body.
    if (event.key === 'Escape' && !event.defaultPrevented && groupsDropdown.value && !hasOpenModal()) {
        event.preventDefault();
        groupsDropdown.value = false;
    }
});
</script>
